import React, { useRef, useState, useEffect, useMemo, useCallback } from "react";
import { Row, Col, Container } from "react-bootstrap";
import Collapsible from "react-collapsible";
import Select, { components } from 'react-select';
import FormLabel from "./FormLabel";

const dropDownStyle = {
	float: "right",
	marginRight: "5px",
	transition: "1s",
};

function compareToSort(a, b) {
	if (a.order < b.order) {
		return -1;
	}
	if (a.order > b.order) {
		return 1;
	}
	return 0;
}

const SelectMultiValueContainer = props => {
	if(props.data.value === '_ALL') { return null }
	return <components.MultiValueContainer {...props}></components.MultiValueContainer>
};

export const AdvancedCol = ({ selectedPreset, onPresetChange, dataType, currentFieldSelection, selectedDataType, onInputChange, clearPreset, setCollapsed, content, needReset, isEditPreset, ...props }) => {
	const currentPreset = useMemo(() => selectedPreset?.config?.dataType === selectedDataType && selectedPreset, [ selectedPreset, selectedDataType ]);

	const isFirstTime = useRef(true); // prevent component loads content in the 2nd time

	const [reactSelectReady, setReactSelectReady] = useState(false);

	const [col0, setCol0] = useState([]);
	const [col1, setCol1] = useState([]);

	const newEmptyValues = useCallback(() => ({
		"amp_phase": [],
		"voltage_phase": [],
		"grid_phase": [],
		"rate": [],
		"grand_total_production": [],
		"EDMI": [],
		"irradiance": [],
		"DGPT2_": [],
		"INV_": [],
		"InvData": [],
		"wind_dir": [],
		"water_level": [],
		"wind_speed": [],
		"BESS_": [],
	}), []);

	const inputsState = useMemo(() => {
		const dirtySelectValues = currentFieldSelection?.[selectedDataType]?.ADVANCED;
		const presetSelectValues = currentPreset?.config?.fields?.ADVANCED;
		return dirtySelectValues || presetSelectValues || newEmptyValues();
	}, [currentFieldSelection, currentPreset, newEmptyValues, selectedDataType]);

	/**
	 * Processing data before render react-select
	 */
	 const processData = useCallback(() => {
		content.sort(compareToSort);
		const quotient = Math.floor(content.length / 2);
		const remainder = content.length % 2;
		let itemsPerCol = 0;
		let indexOfCol = 0;
		remainder > 0 ? (itemsPerCol = quotient + 1) : (itemsPerCol = quotient);

		for (let i = 0; i < content.length; i += itemsPerCol) {
			const tempArray = content.slice(i, i + itemsPerCol);

			switch (indexOfCol) {
				case 0:
					setCol0(tempArray);
					break;
				case 1:
					setCol1(tempArray);
					break;

				default:
					break;
			}
			indexOfCol++;
		}
		setReactSelectReady(true);
	}, [content]);

	// if user is not editing the preset, clear preset
	const clearPresetIfNotEdit = useCallback(() => {
		if(!isEditPreset) {
			clearPreset();
		}
	}, [ isEditPreset, clearPreset ]);

	const allOption = useMemo(() => ({ value: '_ALL', label: 'Select All' }), []);

	const handleSetParentStates = useCallback(newState => {
		const inputChange = [{ section: 'ADVANCED', data: newState }];
		if(selectedPreset?.config?.dataType === selectedDataType) {
			if(selectedPreset?.config?.fields?.STANDARD && !currentFieldSelection[selectedDataType]?.STANDARD) {
				inputChange.push({ section: 'STANDARD', data: selectedPreset.config.fields.STANDARD });
			}
			if(selectedPreset?.config?.fields?.DATE_RANGE && !currentFieldSelection[selectedDataType]?.DATE_RANGE) {
				inputChange.push({ section: 'DATE_RANGE', data: selectedPreset.config.fields.DATE_RANGE });
			}
		}
		onInputChange(inputChange);
		setTimeout(() => clearPresetIfNotEdit());
	}, [clearPresetIfNotEdit, currentFieldSelection, onInputChange, selectedDataType, selectedPreset]);

	const onChangeSelect = useCallback((select, selectedOptions) => {
		let newSelected = selectedOptions ? [...selectedOptions] : [];
		const group = select.children?.[0]?.group;
		const { ...newState } = inputsState;
		// if "select all" option is selected, only store '_ALL' for this group
		if(!inputsState[group].find(option => option.value === '_ALL') && newSelected.find(option => option.value === '_ALL')) {
			newSelected = [allOption];
		}
		// if all options (except "select all") are selected, oly store '_ALL' for this group
		if(!inputsState[group].find(option => option.value === '_ALL') && select.children.every(option => newSelected.find(_option => _option.value === option.value))) {
			newSelected = [allOption];
		}
		// remove "select all" option from state if another option is de-selected
		if(inputsState[group].find(option => option.value === '_ALL') && !select.children.every(option => newSelected.find(_option => _option.value === option.value))) {
			newSelected = newSelected.filter(option => option.value !== '_ALL');
		}
		// only for inverter number
		// if inverter number is selected but inverter data is not selected, auto-select all inverter data for the user
		if(group === 'INV_' && newSelected.length && !inputsState['InvData']?.length) {
			newState['InvData'] = [allOption];
		}
		// only for inverter data
		// if inverter data is selected but inverter number is not selected, auto-select all inverter number for the user
		if(group === 'InvData' && newSelected.length && !inputsState['INV_']?.length) {
			newState['INV_'] = [allOption];
		}

		// update parent state
		newState[group] = newSelected;
		handleSetParentStates(newState);
	}, [handleSetParentStates, inputsState, allOption]);

	// add "Select All" option
	const buildSelectOptions = useCallback(options => {
		const group = options?.[0]?.group;
		if(!options?.length || !group) { return [] }
		if(!inputsState) { return [] }
		const _options = [...options];
		const optionValues = options.map(option => option.value);
		const selectedValues = inputsState[group]?.map(option => option.value) || [];
		// if not yet select all, add "Select All" option
		if(!optionValues.every(optionValue => selectedValues.includes(optionValue)) && optionValues.length - selectedValues.length > 1) {
			_options.unshift(allOption);
		}
		return _options;
	}, [inputsState, allOption]);

	const getSelectValue = useCallback((options) => {
		const group = options?.[0]?.group;
		if(!group) { return [] }
		if(inputsState?.[group]?.find(selectedOption => selectedOption.value === '_ALL')) {
			return [allOption, ...options];
		}
		return inputsState?.[group]?.filter(selectedOption => options.find(option => option.value === selectedOption.value)) || [];
	}, [inputsState, allOption]);

	const selectRendered = useCallback((groupedSelects) => {
		if (!Array.isArray(groupedSelects) || !groupedSelects?.length) return;
		const selects = groupedSelects.map((select, i) => {
			return (
				<div key={select.value} style={{ marginBottom: '1em' }}>
					<Select
						placeholder={`SELECT ${select.label}`}
						options={buildSelectOptions(select.children)}
						// use list.children[0].group instead of list.value, as for "inverter data", list.children[i].group === 'InvData' while list.value === 'inverter_data'
						value={getSelectValue(select.children)}
						onChange={selectedOptions => onChangeSelect(select, selectedOptions)}
						defaultValue={[]}
						isMulti={true}
						isAdvanced={true}
						components={{ MultiValueContainer: SelectMultiValueContainer }}
						styles={{
							container: (provided) => ({
								...provided,
								fontSize: "1.5rem",
								width: "100%",
							}),
							valueContainer: () => ({
								display: "flex",
								flex: "1 1 0%",
								flexWrap: "wrap",
								maxHeight: "60px",
								overflowY: "auto",
							}),
						}}
					/>
				</div>
			);
		});

		return selects;
	}, [buildSelectOptions, onChangeSelect, getSelectValue]);

	useEffect(() => {
		if (isFirstTime.current && content.length > 0) {
			isFirstTime.current = false;
			setCollapsed(false);
			processData();
		}
	}, [isFirstTime, processData, content, setCollapsed]);

	return (
		reactSelectReady && (
			<Collapsible
				trigger={
					<div className="list-group-item">
						<FormLabel popoverHelp={(
							(inputsState.INV_?.length  && !inputsState.InvData?.length) ||
							(!inputsState.INV_?.length && inputsState.InvData?.length)
						) && <div>If you select inverter number, you have to select inverter data, or vice versa</div>}>Advanced Information Column Collection</FormLabel>
						<span
							className={
								!props.isCollapsed
									? "glyphicon glyphicon-menu-down"
									: "glyphicon glyphicon-menu-up"
							}
							aria-hidden="true"
							style={dropDownStyle}
						></span>
					</div>
				}
				transitionTime="200"
				easing="ease-in"
				onTriggerClosing={() => setCollapsed(true)}
				open={!props.isCollapsed}
				onOpening={() => setCollapsed(false)}
				overflowWhenOpen="visible"
			>
				<Container fluid style={{ padding: '1em 1em 0', border: '1px solid #ddd' }}>
					<Row style={{ margin: "0 -0.5em" }}>
						<Col sm={6} style={{ padding: "0 0.5em" }}>{selectRendered(col0)}</Col>
						<Col sm={6} style={{ padding: "0 0.5em" }}>{selectRendered(col1)}</Col>
					</Row>
				</Container>
			</Collapsible>
		)
	);
};
